import { useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { usePwaInstall } from "@/hooks/usePwaInstall";

/**
 * InstallPromptBanner — subtle, dismissible "install FC Staff" banner.
 *
 * - Hidden when already running standalone, already installed, or previously dismissed.
 * - Android/Chrome: triggers native beforeinstallprompt.
 * - iOS Safari: opens a small modal with Share → Add to Home Screen instructions.
 * - Mobile-first spacing; collapses gracefully on desktop.
 */
export default function InstallPromptBanner() {
  const { visible, platform, promptInstall, dismiss } = usePwaInstall();
  const [iosOpen, setIosOpen] = useState(false);

  if (!visible) return null;

  const handleInstall = async () => {
    if (platform === "android") {
      await promptInstall();
    } else if (platform === "ios") {
      setIosOpen(true);
    }
  };

  return (
    <>
      <div
        data-testid="pwa-install-banner"
        className="rounded-2xl border border-brand-primary/10 bg-white shadow-warm px-4 py-3 sm:px-5 sm:py-4 flex items-center gap-3 sm:gap-4"
      >
        <div className="h-10 w-10 shrink-0 rounded-xl bg-terracotta/10 grid place-items-center">
          <Download className="h-5 w-5 text-terracotta" strokeWidth={1.7} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-brand-primary text-sm sm:text-base leading-tight">
            Install FC Staff on your phone
          </p>
          <p className="text-xs text-brand-primary/60 mt-0.5 hidden sm:block">
            Quick access from your home screen — works like a native app.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            onClick={handleInstall}
            data-testid="pwa-install-btn"
            className="bg-terracotta hover:bg-terracotta-hover text-white rounded-full px-4"
          >
            Install
          </Button>
          <button
            onClick={dismiss}
            data-testid="pwa-install-dismiss-btn"
            aria-label="Dismiss install prompt"
            className="h-8 w-8 grid place-items-center rounded-full text-brand-primary/50 hover:text-brand-primary hover:bg-brand-primary/5 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* iOS instructions modal */}
      <Dialog open={iosOpen} onOpenChange={setIosOpen}>
        <DialogContent
          className="sm:max-w-md rounded-2xl"
          data-testid="pwa-ios-instructions"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-brand-primary">
              Install on iPhone
            </DialogTitle>
            <DialogDescription className="text-brand-primary/70">
              Add FC Staff to your home screen in two quick taps.
            </DialogDescription>
          </DialogHeader>

          <ol className="space-y-3 mt-2">
            <li className="flex items-start gap-3">
              <span className="h-7 w-7 rounded-full bg-terracotta/10 text-terracotta font-semibold text-sm grid place-items-center shrink-0">
                1
              </span>
              <p className="text-sm text-brand-primary leading-relaxed pt-0.5">
                Tap the <Share className="inline h-4 w-4 -mt-0.5 mx-0.5 text-brand-primary/70" />
                <strong> Share</strong> button at the bottom of Safari.
              </p>
            </li>
            <li className="flex items-start gap-3">
              <span className="h-7 w-7 rounded-full bg-terracotta/10 text-terracotta font-semibold text-sm grid place-items-center shrink-0">
                2
              </span>
              <p className="text-sm text-brand-primary leading-relaxed pt-0.5">
                Choose <Plus className="inline h-4 w-4 -mt-0.5 mx-0.5 text-brand-primary/70" />
                <strong> Add to Home Screen</strong>, then tap <strong>Add</strong>.
              </p>
            </li>
          </ol>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setIosOpen(false)}
              data-testid="pwa-ios-close-btn"
              className="rounded-full"
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
